import { Injectable, signal } from '@angular/core';

// Telegram WebApp Typen (von telegram-web-app.js)
interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
      photo_url?: string;
      is_premium?: boolean;
    };
    start_param?: string;
    auth_date: number;
    hash: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  MainButton: {
    text: string;
    isVisible: boolean;
    isActive: boolean;
    setText(text: string): void;
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    showProgress(leaveActive?: boolean): void;
    hideProgress(): void;
    onClick(fn: () => void): void;
    offClick(fn: () => void): void;
    setParams(params: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }): void;
  };
  BackButton: {
    isVisible: boolean;
    show(): void;
    hide(): void;
    onClick(fn: () => void): void;
    offClick(fn: () => void): void;
  };
  HapticFeedback: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
  ready(): void;
  expand(): void;
  close(): void;
  sendData(data: string): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

@Injectable({ providedIn: 'root' })
export class TmaService {
  private readonly twa = window.Telegram?.WebApp ?? null;

  readonly available = signal(!!this.twa);
  readonly initData = signal(this.twa?.initData ?? '');
  readonly user = signal(this.twa?.initDataUnsafe?.user ?? null);
  readonly startParam = signal(this.twa?.initDataUnsafe?.start_param ?? null);
  readonly colorScheme = signal<'light' | 'dark'>(this.twa?.colorScheme ?? 'light');
  readonly themeParams = signal<Record<string, string>>(this.twa?.themeParams ?? {});
  readonly isReady = signal(false);

  init(): void {
    if (!this.twa) return;
    this.twa.ready();
    this.twa.expand();
    this.isReady.set(true);
  }

  showMainButton(text: string, onClick: () => void): void {
    if (!this.twa) return;
    this.twa.MainButton.setText(text);
    this.twa.MainButton.onClick(onClick);
    this.twa.MainButton.show();
  }

  hideMainButton(): void {
    this.twa?.MainButton.hide();
  }

  showBackButton(onClick: () => void): void {
    if (!this.twa) return;
    this.twa.BackButton.onClick(onClick);
    this.twa.BackButton.show();
  }

  hideBackButton(): void {
    this.twa?.BackButton.hide();
  }

  haptic(type: 'light' | 'medium' | 'heavy'): void {
    this.twa?.HapticFeedback.impactOccurred(type);
  }

  hapticSuccess(): void {
    this.twa?.HapticFeedback.notificationOccurred('success');
  }

  close(): void {
    this.twa?.close();
  }
}
